import React, { useState, useEffect } from "react";
import { NeoLoader, NeoBadge } from "../../../../../components/Common";
import moment from "moment";
import { useTenant } from "../../../../../hooks/useTenant";
import WorkoutPrescriptionService from "../../../../../services/workout_prescription";

/**
 * WeeklySessionsView
 * Exibe todas as sessões de treino do aluno na semana atual.
 * Sessões concluídas aparecem em tom diferente com badge "Concluído".
 */
const WeeklySessionsView = ({ member }) => {
    const { idTenant } = useTenant();
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [weekOffset, setWeekOffset] = useState(0); // 0 = semana atual, -1 = anterior, +1 = próxima

    useEffect(() => {
        const fetchSessions = async () => {
            if (!member?.id || !idTenant) return;
            setLoading(true);
            try {
                const allSessions = await WorkoutPrescriptionService.getAllSessionsByMember(idTenant, member.id);
                setSessions(allSessions);
            } catch (error) {
                console.error("WeeklySessionsView -> Erro:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchSessions();
    }, [member?.id, idTenant]);

    // Calcula início e fim da semana com offset
    const weekStart = moment().startOf("isoWeek").add(weekOffset, "weeks");
    const weekEnd = moment(weekStart).endOf("isoWeek");

    // Filtra sessões da semana selecionada
    const weekSessions = sessions.filter(s => {
        if (!s.scheduledDate) return false;
        return s.scheduledDate >= weekStart.format("YYYY-MM-DD") && s.scheduledDate <= weekEnd.format("YYYY-MM-DD");
    });

    // Agrupa por dia da semana
    const dayNames = ["Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado", "Domingo"];
    const sessionsByDay = {};
    weekSessions.forEach(s => {
        const dayIndex = moment(s.scheduledDate).isoWeekday() - 1; // 0=Seg, 6=Dom
        if (!sessionsByDay[dayIndex]) sessionsByDay[dayIndex] = [];
        sessionsByDay[dayIndex].push(s);
    });

    const formatDateBR = (dateStr) => {
        if (!dateStr) return "";
        const parts = dateStr.split("-");
        if (parts.length === 3) return `${parts[2]}/${parts[1]}`;
        return dateStr;
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center py-5">
                <NeoLoader size="sm" className="me-2" />
                <span className="text-muted">Carregando sessões da semana...</span>
            </div>
        );
    }

    if (sessions.length === 0) {
        return (
            <div className="text-center py-5">
                <i className="mdi mdi-calendar-blank-outline text-muted" style={{ fontSize: "48px" }} />
                <h6 className="text-muted mt-3">Nenhuma sessão de treino</h6>
                <p className="text-muted font-size-13">Prescreva um treino para este aluno para gerar as sessões.</p>
            </div>
        );
    }

    return (
        <div>
            {/* Navegação da Semana */}
            <div className="d-flex align-items-center justify-content-between mb-4 p-3 bg-light rounded">
                <button
                    className="btn btn-sm btn-outline-primary rounded-circle d-flex align-items-center justify-content-center"
                    style={{ width: 32, height: 32 }}
                    onClick={() => setWeekOffset(prev => prev - 1)}
                >
                    <i className="mdi mdi-chevron-left font-size-18" />
                </button>
                <div className="text-center">
                    <h6 className="mb-0 fw-bold text-dark">
                        {weekOffset === 0 ? "Esta Semana" : weekOffset === -1 ? "Semana Passada" : weekOffset === 1 ? "Próxima Semana" : `Semana ${weekStart.format("DD/MM")} - ${weekEnd.format("DD/MM")}`}
                    </h6>
                    <small className="text-muted">
                        {weekStart.format("DD/MM/YYYY")} — {weekEnd.format("DD/MM/YYYY")}
                    </small>
                </div>
                <button
                    className="btn btn-sm btn-outline-primary rounded-circle d-flex align-items-center justify-content-center"
                    style={{ width: 32, height: 32 }}
                    onClick={() => setWeekOffset(prev => prev + 1)}
                >
                    <i className="mdi mdi-chevron-right font-size-18" />
                </button>
            </div>

            {/* Cards por dia */}
            {weekSessions.length === 0 ? (
                <div className="text-center py-4">
                    <i className="mdi mdi-calendar-remove-outline text-muted font-size-40 d-block mb-2" />
                    <p className="text-muted font-size-13 mb-0">Nenhuma sessão prevista para esta semana.</p>
                </div>
            ) : (
                <div className="row g-3">
                    {dayNames.map((dayName, dayIndex) => {
                        const daySessions = sessionsByDay[dayIndex] || [];
                        if (daySessions.length === 0) return null;

                        const dayDate = moment(weekStart).add(dayIndex, "days");

                        return daySessions.map((session) => {
                            const isCompleted = session.status === "completed";
                            const isToday = dayDate.isSame(moment(), "day");
                            const isPast = dayDate.isBefore(moment(), "day") && !isCompleted;

                            return (
                                <div className="col-12 col-md-6 col-lg-4" key={session.id}>
                                    <div
                                        className={`neo-block-card shadow-sm h-100 ${isCompleted ? "opacity-75" : ""}`}
                                        style={{
                                            borderTop: `4px solid ${isCompleted ? "#28a745" : isToday ? "#3b5de7" : isPast ? "#dc3545" : "#6c757d"}`,
                                            backgroundColor: isCompleted ? "#f0fdf4" : "white",
                                        }}
                                    >
                                        <div className="p-3">
                                            {/* Cabeçalho: dia + datas */}
                                            <div className="d-flex justify-content-between align-items-start mb-2">
                                                <div>
                                                    <span className={`font-size-11 fw-bold text-uppercase ${isToday ? "text-primary" : "text-muted"}`}>
                                                        {dayName} {formatDateBR(session.scheduledDate)}
                                                    </span>
                                                    {isToday && (
                                                        <NeoBadge variant="soft-primary" className="ms-2">Hoje</NeoBadge>
                                                    )}
                                                </div>
                                                <div className="text-end">
                                                    {isCompleted ? (
                                                        <NeoBadge variant="soft-success"><i className="mdi mdi-check-circle me-1" />Concluído</NeoBadge>
                                                    ) : isPast ? (
                                                        <NeoBadge variant="soft-danger">Atrasado</NeoBadge>
                                                    ) : (
                                                        <NeoBadge variant="soft-secondary">Pendente</NeoBadge>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Nome do Treino */}
                                            <h6 className={`fw-bold mb-1 ${isCompleted ? "text-success" : "text-dark"}`} style={{ fontSize: "15px" }}>
                                                {session.workoutName || `Treino ${session.workoutLetter}`}
                                            </h6>

                                            {/* Prescrição de origem */}
                                            <p className="text-muted font-size-11 mb-2">
                                                <i className="mdi mdi-folder-outline me-1" />
                                                {session.templateTitle || "Prescrição"}
                                                <span className="ms-2">
                                                    <i className="mdi mdi-pound me-1" />
                                                    Sessão {session.sessionNumber}/{session.totalSessions}
                                                </span>
                                            </p>

                                            {/* Datas prevista vs realizada */}
                                            <div className="border-top pt-2 mt-2">
                                                <div className="d-flex justify-content-between font-size-11">
                                                    <span className="text-muted">
                                                        <i className="mdi mdi-calendar-clock me-1" />
                                                        Previsto: <strong>{formatDateBR(session.scheduledDate)}</strong>
                                                    </span>
                                                    {isCompleted && session.completedAt && (
                                                        <span className="text-success">
                                                            <i className="mdi mdi-check me-1" />
                                                            Feito: <strong>
                                                                {session.completedAt?.toDate
                                                                    ? moment(session.completedAt.toDate()).format("DD/MM")
                                                                    : moment(session.completedAt).format("DD/MM")}
                                                            </strong>
                                                        </span>
                                                    )}
                                                </div>
                                                {isCompleted && session.completedVia && (
                                                    <span className="font-size-10 text-muted">
                                                        via {session.completedVia === "kiosk_print" ? "Totem" : session.completedVia === "app" ? "App" : "Manual"}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        });
                    })}
                </div>
            )}

            {/* Resumo da semana */}
            <div className="mt-4 p-3 bg-light rounded d-flex justify-content-around text-center">
                <div>
                    <h5 className="fw-bold text-dark mb-0">{weekSessions.length}</h5>
                    <small className="text-muted">Sessões</small>
                </div>
                <div>
                    <h5 className="fw-bold text-success mb-0">{weekSessions.filter(s => s.status === "completed").length}</h5>
                    <small className="text-muted">Concluídas</small>
                </div>
                <div>
                    <h5 className="fw-bold text-warning mb-0">{weekSessions.filter(s => s.status === "pending").length}</h5>
                    <small className="text-muted">Pendentes</small>
                </div>
            </div>
        </div>
    );
};

export default WeeklySessionsView;
